import * as React from 'react'
import * as PropTypes from 'prop-types'
import styled, { css } from 'styled-components'

import Avatar from '../../../../components/ui/Avatar/Avatar'
import Icon from '../../../../components/ui/Icon/Icon'
import TextInput from '../../../../components/ui/TextInput/TextInput'
import ActionButton from '../../../../components/ui/ActionButton/ActionButton'

import mobileNumberInput from '../../../../services/input/mobileNumberInput'

const AvatarSection = styled.div`
  margin: 37px 0;
`

const AvatarContainer = styled.div`
  position: relative;
  height: 120px;
  width: 120px;
  margin: 0 auto;
  border-radius: 120px;

  ${props =>
    props.hasBackground &&
    css`
      background-color: var(--color-primary);
      padding: 1rem;
    `}
`

const ButtonWrapper = styled.div`
  margin: 1rem 0 0;
`

const StyledButton = styled(ActionButton)`
  width: 100%;
`

const StyledLink = styled(ActionButton)`
  width: 100%;
  border: none;
  color: var(--color-fg);
  font-size: var(--size-root);
  font-weight: var(--font-weight);
`

const WithBadge = styled.div`
  position: relative;
`

const ProfileBadge = styled.span`
  top: 26px;
  right: 10px;
  position: absolute;
  font-size: 0.625rem;
  text-align: center;
  text-transform: uppercase;
  color: var(--color-primary);
`

const EmailTextInput = styled(TextInput)`
  ${props =>
    props.variant === 'withIcon' &&
    css`
      padding-right: 3.5rem;
    `}
`

const VerifyEmailMessage = styled.p`
  color: #ff4f83;
  font-size: 0.625rem;
  line-height: 0.875rem;
  margin-top: -7px;
`

const EmailNotifMessage = styled.p`
  color: var(--color-primary);
  font-size: 0.625rem;
  line-height: 0.875rem;
  margin-top: -7px;
`

const StyledAvatar = styled(Avatar)`
  height: 100%;
  width: 100%;
`

const CameraLink = styled.div`
  cursor: pointer;
  position: absolute;
  bottom: 0;
  right: 0;
  height: 1.9375rem;
  width: 1.9375rem;
  background: var(--color-primary);
  border: 2px solid white;
  border-radius: 1.9375rem;
  display: flex;
  color: white;
  text-align: center;
  font-size: 0.8125rem;
  align-items: center;
  justify-content: center;
  box-shadow: var(--box-shadow);
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
`

function UpdateProfilePageForm(props) {
  const {
    handleSubmit,
    handleChange,
    handleBlur,
    values,
    errors,
    touched,
    isSubmitting,
    isValid,
    disabled,
    method,
    action,
    disableEmailField,
    emailVerificationStatus,
    onCameraClick,
    avatarUrl,
    recoveryForm,
    push,
    displayProfileModal,
  } = props

  const [currentEmail] = React.useState(values.email)

  const handleDeleteAccount = () => {
    displayProfileModal && displayProfileModal(false)
    push('/delete-account')
  }

  return (
    <>
      <AvatarSection>
        <AvatarContainer hasBackground={!avatarUrl}>
          <StyledAvatar src={avatarUrl} />
          {!recoveryForm && (
            <CameraLink onClick={onCameraClick}>
              <Icon name="camera" />
            </CameraLink>
          )}
        </AvatarContainer>
      </AvatarSection>
      <form
        onSubmit={
          !errors.email && disableEmailField && values.email !== currentEmail
            ? e => e.preventDefault()
            : handleSubmit
        }
        method={method}
        action={action}
        autoComplete="off"
      >
        <fieldset disabled={isSubmitting || disabled}>
          <TextInput
            name="firstName"
            type="text"
            placeholder="First Name (Max. of 25 characters)"
            initialValue={values.firstName}
            error={errors.firstName}
            touched={touched.firstName}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={recoveryForm}
            maxLength={25}
          />
          <TextInput
            name="lastName"
            type="text"
            placeholder="Last Name (Max. of 25 characters)"
            initialValue={values.lastName}
            error={errors.lastName}
            touched={touched.lastName}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={recoveryForm}
            maxLength={25}
          />
          <WithBadge>
            <EmailTextInput
              name="email"
              type="email"
              placeholder="Email Address"
              initialValue={values.email}
              error={errors.email}
              touched={touched.email}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={disableEmailField || recoveryForm}
              readonly
              variant={
                emailVerificationStatus === 2 && values.email === currentEmail
                  ? 'withIcon'
                  : ''
              }
              iconName=""
            />
            {emailVerificationStatus === 2 && values.email === currentEmail && (
              <ProfileBadge>Verified</ProfileBadge>
            )}
          </WithBadge>
          {!errors.email &&
            emailVerificationStatus !== 2 &&
            values.email === currentEmail && (
              <VerifyEmailMessage>
                Email not verified. Please check the verification link sent to
                your email
              </VerifyEmailMessage>
            )}
          {!errors.email &&
            disableEmailField &&
            values.email !== currentEmail && (
              <EmailNotifMessage>Email cannot be updated</EmailNotifMessage>
            )}
          <TextInput
            name="mobileNumber"
            type="text"
            placeholder={`${
              values.mobileNumber ? '' : 'Enter 11-digit '
            }Mobile Number`}
            maxLength={11}
            initialValue={values.mobileNumber}
            error={errors.mobileNumber}
            touched={touched.mobileNumber}
            onChange={e => mobileNumberInput(e, handleChange)}
            onBlur={handleBlur}
          />
          <ButtonWrapper>
            <StyledButton
              type="submit"
              variant="primary"
              disabled={
                disableEmailField && currentEmail !== values.email
                  ? true
                  : !isValid
              }
            >
              Update
            </StyledButton>
          </ButtonWrapper>
          <ButtonWrapper>
            <StyledLink type="button" onClick={handleDeleteAccount}>
              Delete Account
            </StyledLink>
          </ButtonWrapper>
        </fieldset>
      </form>
    </>
  )
}

UpdateProfilePageForm.defaultProps = {
  handleSubmit: null,
  handleChange: null,
  handleBlur: null,
  values: {},
  errors: {},
  touched: {},
  isSubmitting: false,
  isValid: false,
  disabled: false,
  method: 'get',
  action: '',
}

UpdateProfilePageForm.propTypes = {
  handleSubmit: PropTypes.func,
  handleChange: PropTypes.func,
  handleBlur: PropTypes.func,
  values: PropTypes.shape(),
  errors: PropTypes.shape(),
  touched: PropTypes.shape(),
  isSubmitting: PropTypes.bool,
  isValid: PropTypes.bool,
  disabled: PropTypes.bool,
  method: PropTypes.oneOf(['get', 'post']),
  action: PropTypes.string,
}

export default UpdateProfilePageForm
